/**
 * Toast Utility
 * Wrapper around Sonner for consistent toast notifications
 *
 * Usage:
 * import { showToast, showToastWithAction } from '@/components/ui/Toast'
 *
 * // Simple toast
 * showToast('success', 'Your changes have been saved')
 * showToast('error', 'Something went wrong', 'Error')
 *
 * // Toast with action button
 * showToastWithAction('info', 'Item added to cart', {
 *   label: 'View Cart',
 *   onClick: () => router.push('/checkout')
 * })
 *
 * Or use Sonner directly:
 * import { toast } from 'sonner'
 * toast.success('Message')
 */

import { toast as sonnerToast } from 'sonner'

export const toast = sonnerToast

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastOptions {
  duration?: number
  action?: ToastAction
}

/**
 * Show a simple toast notification
 */
export function showToast(
  type: ToastType,
  description: string,
  title?: string
) {
  const message = title ? `${title}: ${description}` : description

  switch (type) {
    case 'success':
      sonnerToast.success(message)
      break
    case 'error':
      sonnerToast.error(message)
      break
    case 'warning':
      sonnerToast.warning(message)
      break
    case 'info':
      sonnerToast.info(message)
      break
  }
}

/**
 * Show a toast notification with an action button
 * Great for "View Cart", "View Orders", "Undo" actions
 */
export function showToastWithAction(
  type: ToastType,
  description: string,
  action: ToastAction,
  options?: { duration?: number }
) {
  const toastOptions = {
    action: {
      label: action.label,
      onClick: action.onClick,
    },
    duration: options?.duration ?? 5000, // Default 5 seconds for action toasts
  }

  switch (type) {
    case 'success':
      sonnerToast.success(description, toastOptions)
      break
    case 'error':
      sonnerToast.error(description, toastOptions)
      break
    case 'warning':
      sonnerToast.warning(description, toastOptions)
      break
    case 'info':
      sonnerToast.info(description, toastOptions)
      break
  }
}

/**
 * Predefined toast messages with actions for common scenarios
 */
export const toastMessages = {
  // Cart actions
  addedToCart: (onViewCart: () => void) =>
    showToastWithAction('success', 'Hosting added to cart', {
      label: 'View Cart',
      onClick: onViewCart,
    }),

  alreadyInCart: (onViewCart: () => void) =>
    showToastWithAction('info', 'This invitation is already in your cart', {
      label: 'View Cart',
      onClick: onViewCart,
    }),

  alreadyLive: (onViewOrders: () => void) =>
    showToastWithAction('info', 'This invitation is already live', {
      label: 'View Orders',
      onClick: onViewOrders,
    }),

  // Checkout success
  checkoutComplete: (onViewInvitation: () => void) =>
    showToastWithAction('success', 'Your invitation is now live!', {
      label: 'View Invitation',
      onClick: onViewInvitation,
    }),

  // Race condition (good news!)
  alreadyActivated: (onViewOrders: () => void) =>
    showToastWithAction(
      'success',
      'Good news! This invitation was already activated',
      {
        label: 'View Orders',
        onClick: onViewOrders,
      }
    ),

  // Cart expired
  cartExpired: (onRefresh: () => void) =>
    showToastWithAction('warning', 'Your cart has expired', {
      label: 'Refresh',
      onClick: onRefresh,
    }),

  // Invitation deleted
  invitationDeleted: (onRefresh: () => void) =>
    showToastWithAction('error', 'This invitation no longer exists', {
      label: 'Refresh',
      onClick: onRefresh,
    }),
}

export function useToast() {
  return {
    success: (description: string, title?: string) =>
      showToast('success', description, title),
    error: (description: string, title?: string) =>
      showToast('error', description, title),
    warning: (description: string, title?: string) =>
      showToast('warning', description, title),
    info: (description: string, title?: string) =>
      showToast('info', description, title),
    withAction: showToastWithAction,
    messages: toastMessages,
  }
}
